import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useMapStore } from '@/store/mapStore';
import { PlaceCategory } from '@/types/places';

export interface PlaceSummary {
  id: string;
  name: string;
  category: PlaceCategory;
  lat: number;
  lng: number;
  address: string;
  city: string;
  rating: number | null;
  review_count: number | null;
  cover_photo: string | null;
  sponsorship_tier: string | null;
  distance_km: number | null;
  is_featured?: boolean;
}

interface UsePlacesOptions {
  category?: PlaceCategory | null;
  limit?: number;
}

export const usePlaces = (options?: UsePlacesOptions) => {
  const { userLocation, activeCategories, activeKosherStatuses, activeKosherAuthorities } = useMapStore();
  
  // Create stable filter key for proper cache invalidation
  const filtersKey = useMemo(() => JSON.stringify({
    categories: [...activeCategories].sort(),
    statuses: [...activeKosherStatuses].sort(),
    authorities: [...activeKosherAuthorities].sort()
  }), [activeCategories, activeKosherStatuses, activeKosherAuthorities]);
  
  // Stabilize user location
  const stableUserLocation = useMemo(() => userLocation, [
    userLocation?.[0], 
    userLocation?.[1]
  ]);
  
  return useQuery({
    queryKey: ['places', options?.category, stableUserLocation, filtersKey],
    queryFn: async () => {
      console.log('[usePlaces] Fetching with filters:', { 
        categories: activeCategories, 
        statuses: activeKosherStatuses, 
        authorities: activeKosherAuthorities,
        location: stableUserLocation 
      });
      
      // Merge options category with activeCategories
      const categoryFilters = options?.category 
        ? [options.category] 
        : activeCategories.length > 0 ? activeCategories : null;
      
      const { data, error } = await supabase.rpc('search_places_v2', {
        search_query: null,
        category_filters: categoryFilters,
        city_filter: null,
        user_lat: stableUserLocation ? stableUserLocation[1] : null,
        user_lng: stableUserLocation ? stableUserLocation[0] : null,
        radius_km: stableUserLocation ? (categoryFilters ? 50 : 50000) : null,
        limit_count: options?.limit ?? 500,
        kosher_statuses: activeKosherStatuses.length > 0 ? activeKosherStatuses : null,
        kosher_authorities: activeKosherAuthorities.length > 0 ? activeKosherAuthorities : null,
      });

      if (error) throw error;
      
      console.log('[usePlaces] Received', data?.length || 0, 'places');
      return (data as PlaceSummary[]) || [];
    },
    staleTime: 1000,
  });
};
